"use client";

import { useEffect, useState } from "react";
import {
  Checkbox,
  CopyButton,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  SELECT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { randomToken } from "@/lib/tool-kit/study/dates";
import {
  MAX_TASKS,
  type MatrixTask,
  matrixText,
  moveTask,
  parseSavedTasks,
  parseTaskLines,
  type Quadrant,
  quadrantFor,
} from "@/lib/tool-kit/study-more/eisenhower";

const STORAGE_KEY = "citationeasy-eisenhower-matrix-v1";

/** Reading order of the grid: important row first, urgent column first. */
const GRID_ORDER: readonly Quadrant[] = [
  "do",
  "schedule",
  "delegate",
  "eliminate",
];

const BOX_STYLE: Record<Quadrant, string> = {
  do: "border-primary/40 bg-primary/5",
  schedule: "border-border bg-card",
  delegate: "border-border bg-card",
  eliminate: "border-border bg-muted/40",
};

/** English strings. A translated content file overrides these through `ui`. */
export const EISENHOWER_MATRIX_STRINGS = {
  addHeading: "Add tasks",
  tasksLabel: "Tasks — one per line",
  tasksPlaceholder:
    "Email my supervisor\nStart the literature review\nReply to the group chat",
  tasksHint:
    "Answer the two questions below and every line goes into the matching box. You can drag a task to another box afterwards.",
  urgentLabel:
    "These are urgent — they need attention soon or someone is waiting",
  importantLabel: "These are important — they move me toward my own goals",
  goesTo: "These will go in: {box}",
  addButton: "Add to matrix",
  limitReached:
    "The matrix holds up to {max} tasks. Remove or finish some to add more.",
  matrixHeading: "Your matrix",
  urgentColumn: "Urgent",
  notUrgentColumn: "Not urgent",
  importantRow: "Important",
  notImportantRow: "Not important",
  boxes: {
    do: "Do first",
    schedule: "Schedule",
    delegate: "Delegate",
    eliminate: "Eliminate",
  },
  boxAdvice: {
    do: "Urgent and important: do these now.",
    schedule: "Important, not urgent: book time for these.",
    delegate: "Urgent, not important: hand off, or keep short.",
    eliminate: "Neither: drop, or save for spare time.",
  },
  taskCount: "one:{count} task|other:{count} tasks",
  emptyBox: "Nothing here yet.",
  doneLabel: "Mark done: {task}",
  moveLabel: "Move “{task}” to",
  removeLabel: "Remove {task}",
  dropHint: "Drag a task here to move it to {box}.",
  copyText: "Copy as text",
  copied: "Copied!",
  downloadTxt: "Download .txt",
  txtFileName: "eisenhower-matrix.txt",
  doneMark: "[done]",
  print: "Print matrix",
  clearDone: "Clear finished tasks",
  clearAll: "Clear all",
  confirmClear: "Click again to clear everything",
  storageNote:
    "Your tasks are saved in this browser's local storage — nothing is uploaded. They stay on this device and browser, and clearing site data erases them.",
  storageUnavailable:
    "This browser is not letting the page save data, so your tasks will be gone when you close the page. Copy or download them first.",
  printTitle: "Eisenhower matrix",
} as const;

export default function EisenhowerMatrixWidget() {
  const strings = useToolUi(EISENHOWER_MATRIX_STRINGS);
  const locale = useToolLocale();
  const [tasks, setTasks] = useState<MatrixTask[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [draft, setDraft] = useState("");
  const [urgent, setUrgent] = useState(true);
  const [important, setImportant] = useState(true);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<Quadrant | null>(null);
  const [clearArmed, setClearArmed] = useState(false);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      setCanSave(false);
    }
    setTasks(parseSavedTasks(raw));
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!(loaded && canSave)) {
      return;
    }
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
      } catch {
        setCanSave(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [loaded, canSave, tasks]);

  const target = quadrantFor(urgent, important);
  const room = MAX_TASKS - tasks.length;
  const lines = parseTaskLines(draft);
  const labels = strings.boxes;
  const exportText = matrixText(tasks, labels, strings.doneMark);

  function addTasks() {
    if (lines.length === 0 || room <= 0) {
      return;
    }
    const added = lines.slice(0, room).map((text) => ({
      id: randomToken(8),
      text,
      quadrant: target,
      done: false,
    }));
    setTasks([...tasks, ...added]);
    setDraft("");
  }

  function drop(quadrant: Quadrant) {
    if (dragging) {
      setTasks(moveTask(tasks, dragging, quadrant));
    }
    setDragging(null);
    setOver(null);
  }

  function clearAll() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setTasks([]);
    setClearArmed(false);
  }

  return (
    <div className="space-y-8">
      <section className="space-y-4 print:hidden">
        <h2 className={WIDGET_H2}>{strings.addHeading}</h2>
        <TextAreaField
          hint={strings.tasksHint}
          id="em-tasks"
          label={strings.tasksLabel}
          onChange={setDraft}
          placeholder={strings.tasksPlaceholder}
          prose
          rows={3}
          value={draft}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={urgent}
            label={strings.urgentLabel}
            onChange={setUrgent}
          />
          <Checkbox
            checked={important}
            label={strings.importantLabel}
            onChange={setImportant}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={lines.length === 0 || room <= 0}
            onClick={addTasks}
            type="button"
          >
            {strings.addButton}
          </button>
          <span aria-live="polite" className={HINT}>
            {t(strings.goesTo, { box: labels[target] })}
          </span>
        </div>
        {room <= 0 ? (
          <p className={HINT}>{t(strings.limitReached, { max: MAX_TASKS })}</p>
        ) : null}
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>
          <span className="print:hidden">{strings.matrixHeading}</span>
          <span className="hidden print:inline">{strings.printTitle}</span>
        </h2>
        <div className="grid grid-cols-[auto_1fr_1fr] gap-2">
          <span />
          <p className="text-center font-medium text-foreground text-sm">
            {strings.urgentColumn}
          </p>
          <p className="text-center font-medium text-foreground text-sm">
            {strings.notUrgentColumn}
          </p>
          {[0, 1].map((row) => (
            <div className="contents" key={row}>
              <p className="flex rotate-180 items-center font-medium text-foreground text-sm [writing-mode:vertical-rl]">
                {row === 0 ? strings.importantRow : strings.notImportantRow}
              </p>
              {[0, 1].map((column) => {
                const quadrant = quadrantFor(column === 0, row === 0);
                const inBox = tasks.filter(
                  (task) => task.quadrant === quadrant
                );
                return (
                  // biome-ignore lint/a11y/noStaticElementInteractions: a drop target; every task also has a Move select for keyboard users
                  // biome-ignore lint/a11y/noNoninteractiveElementInteractions: same drop target
                  <div
                    className={`min-h-48 rounded-lg border p-3 transition-colors print:break-inside-avoid ${BOX_STYLE[quadrant]} ${
                      over === quadrant ? "ring-2 ring-primary" : ""
                    }`}
                    key={quadrant}
                    onDragLeave={() => setOver(null)}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setOver(quadrant);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      drop(quadrant);
                    }}
                  >
                    <div className="mb-2">
                      <h3 className="font-semibold text-foreground">
                        {labels[quadrant]}{" "}
                        <span className="font-normal text-muted-foreground text-xs">
                          {tPlural(strings.taskCount, locale, inBox.length)}
                        </span>
                      </h3>
                      <p className={HINT}>{strings.boxAdvice[quadrant]}</p>
                    </div>
                    {inBox.length === 0 ? (
                      <p className="text-muted-foreground text-sm print:hidden">
                        {tasks.length > 0
                          ? t(strings.dropHint, { box: labels[quadrant] })
                          : strings.emptyBox}
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {inBox.map((task) => (
                          // biome-ignore lint/a11y/noNoninteractiveElementInteractions: drag is a pointer shortcut; each task has a Move select for keyboard users
                          <li
                            className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                            draggable
                            key={task.id}
                            onDragEnd={() => {
                              setDragging(null);
                              setOver(null);
                            }}
                            onDragStart={(event) => {
                              setDragging(task.id);
                              event.dataTransfer.setData("text/plain", task.id);
                              event.dataTransfer.effectAllowed = "move";
                            }}
                          >
                            <input
                              aria-label={t(strings.doneLabel, {
                                task: task.text,
                              })}
                              checked={task.done}
                              className="accent-primary"
                              onChange={(event) =>
                                setTasks(
                                  tasks.map((item) =>
                                    item.id === task.id
                                      ? { ...item, done: event.target.checked }
                                      : item
                                  )
                                )
                              }
                              type="checkbox"
                            />
                            <span
                              className={`min-w-0 flex-1 break-words ${
                                task.done
                                  ? "text-muted-foreground line-through"
                                  : "text-foreground"
                              }`}
                            >
                              {task.text}
                            </span>
                            <select
                              aria-label={t(strings.moveLabel, {
                                task: task.text,
                              })}
                              className={`${SELECT} w-auto py-1 text-xs print:hidden`}
                              onChange={(event) =>
                                setTasks(
                                  moveTask(
                                    tasks,
                                    task.id,
                                    event.target.value as Quadrant
                                  )
                                )
                              }
                              value={task.quadrant}
                            >
                              {GRID_ORDER.map((key) => (
                                <option key={key} value={key}>
                                  {labels[key]}
                                </option>
                              ))}
                            </select>
                            <button
                              aria-label={t(strings.removeLabel, {
                                task: task.text,
                              })}
                              className={`${BUTTON_SMALL} print:hidden`}
                              onClick={() =>
                                setTasks(
                                  tasks.filter((item) => item.id !== task.id)
                                )
                              }
                              type="button"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      <section className={`${CARD} space-y-3 print:hidden`}>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyText}
            text={tasks.length > 0 ? exportText : ""}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={tasks.length === 0}
            onClick={() => downloadText(strings.txtFileName, exportText)}
            type="button"
          >
            {strings.downloadTxt}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!tasks.some((task) => task.done)}
            onClick={() => setTasks(tasks.filter((task) => !task.done))}
            type="button"
          >
            {strings.clearDone}
          </button>
          <button
            className={BUTTON_SMALL}
            disabled={tasks.length === 0}
            onClick={clearAll}
            type="button"
          >
            {clearArmed ? strings.confirmClear : strings.clearAll}
          </button>
        </div>
        <p className={HINT}>
          {canSave ? strings.storageNote : strings.storageUnavailable}
        </p>
      </section>
    </div>
  );
}
